import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, ChevronDown, ChevronLeft, Globe } from 'lucide-react';
import { SheetBackButton } from './SheetBackButton';

export type ShoppingCartAssistantHeaderBarProps = {
  t: (key: string) => string;
  isMinimized: boolean;
  isPhone: boolean;
  effectiveHovered: boolean;
  topOffset: number;
  handleMinimizeToggle: (nextMinimized: boolean) => void;
  onCartClick?: () => void;
  cartItemCount: number;
  language: string;
  setLanguage: (lang: 'fr' | 'en' | 'zh') => void;
  languageTriggerRef: React.RefObject<HTMLButtonElement | null>;
  languageMenuRef: React.RefObject<HTMLDivElement | null>;
  isLanguageQuickOpen: boolean;
  setIsLanguageQuickOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLanguageMenuVisible: boolean;
  languageOptions: Array<{ code: 'fr' | 'en' | 'zh'; label: string }>;
};

export function ShoppingCartAssistantHeaderBar({
  t,
  isMinimized,
  isPhone,
  effectiveHovered,
  topOffset,
  handleMinimizeToggle,
  onCartClick,
  cartItemCount,
  language,
  setLanguage,
  languageTriggerRef,
  languageMenuRef,
  isLanguageQuickOpen,
  setIsLanguageQuickOpen,
  isLanguageMenuVisible,
  languageOptions,
}: ShoppingCartAssistantHeaderBarProps) {
  return (
    <div
      className={`relative flex flex-shrink-0 items-center justify-between border-b transition-all duration-300 ${
        isMinimized
          ? `p-2 ${effectiveHovered ? 'border-black/20 text-[#3d2817]' : 'border-white/75 text-white'}`
          : 'border-black/10 bg-[#ece3de] p-3 text-[#3d2817]'
      }`}
      style={{ zIndex: 61 }}
    >
      {!isMinimized && (
        <div className="flex w-full min-w-0 items-center gap-2">
          <SheetBackButton
            phone="inline"
            onClick={() => handleMinimizeToggle(true)}
          />
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="flex-shrink-0 rounded-lg bg-black/10 p-2">
              <Calendar className="h-5 w-5 text-[#3d2817]" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-base font-bold text-[#3d2817] md:text-lg">
                {t('shoppingCart.updateTravelInfo') || t('shoppingCart.travelDuration')}
              </h2>
              <p className="truncate text-xs text-[#5d4437]">{t('shoppingCart.selectDates')}</p>
            </div>
          </div>
        </div>
      )}
      {isMinimized && (
        <div
          className="relative flex h-full w-full cursor-default flex-col items-center justify-between"
          style={{
            minHeight: isPhone ? 'auto' : `calc(100vh - ${topOffset + 6}px)`,
          }}
        >
          <div className="flex flex-col items-center space-y-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleMinimizeToggle(false);
              }}
              className={`rounded p-2 transition-colors ${effectiveHovered ? 'text-[#3d2817] hover:bg-black/10 hover:text-gray-800' : 'text-white hover:bg-white/20 hover:text-gray-200'}`}
              title={t('shoppingCart.expand')}
              aria-label={t('shoppingCart.expand')}
            >
              <Calendar className={`h-5 w-5 ${effectiveHovered ? 'text-[#3d2817]' : 'text-white'}`} />
            </button>

            {onCartClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onCartClick();
                }}
                className={`relative rounded p-2 transition-colors ${effectiveHovered ? 'text-[#3d2817] hover:bg-black/10 hover:text-gray-800' : 'text-white hover:bg-white/20 hover:text-gray-200'}`}
                title={t('shoppingCart.myCart')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`h-5 w-5 ${effectiveHovered ? 'text-[#3d2817]' : 'text-white'}`}
                >
                  <circle cx="8" cy="21" r="1" />
                  <circle cx="19" cy="21" r="1" />
                  <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
                </svg>
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
                    {cartItemCount > 999 ? '999+' : cartItemCount}
                  </span>
                )}
              </button>
            )}
          </div>

          <div
            className="relative z-[75] mb-6 flex flex-col items-center space-y-2"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <button
              ref={languageTriggerRef as React.LegacyRef<HTMLButtonElement>}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsLanguageQuickOpen((prev) => !prev);
              }}
              className={`group relative h-10 w-[70px] overflow-hidden rounded-full border transition-all duration-300 ${
                effectiveHovered ? 'border-black/25 bg-black/5' : 'border-white/35 bg-black/35'
              }`}
              aria-label={t('common.language') || 'Language'}
            >
              <span className="absolute inset-0 flex items-center justify-center">
                <span
                  className={`absolute left-1 flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300 ${
                    effectiveHovered ? 'bg-[#3d2817]/15' : 'bg-white/20'
                  } ${isLanguageMenuVisible ? 'w-[60px]' : ''}`}
                >
                  <Globe className={`h-4 w-4 ${effectiveHovered ? 'text-[#3d2817]' : 'text-white'}`} />
                </span>
                <span
                  className={`absolute right-2 text-xs font-semibold transition-all duration-300 ${
                    effectiveHovered ? 'text-[#3d2817]' : 'text-white'
                  } ${isLanguageMenuVisible ? 'translate-x-2 opacity-0' : 'opacity-100'}`}
                >
                  {language.toUpperCase()}
                </span>
                <span
                  className={`absolute right-2 transition-all duration-300 ${
                    effectiveHovered ? 'text-[#3d2817]' : 'text-white'
                  } ${isLanguageMenuVisible ? 'opacity-100' : 'opacity-0'}`}
                >
                  {isLanguageMenuVisible ? <ChevronLeft className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </span>
              </span>
            </button>
            <AnimatePresence initial={false} mode="wait">
              {isLanguageMenuVisible && (
                <motion.div
                  key="language-popout"
                  ref={languageMenuRef as React.LegacyRef<HTMLDivElement>}
                  initial={{ opacity: 0, x: -8, width: 72 }}
                  animate={{ opacity: 1, x: 0, width: 190 }}
                  exit={{ opacity: 0, x: -8, width: 72 }}
                  transition={{ duration: 0.24, ease: 'easeOut' }}
                  className={`absolute top-0 left-full z-[80] ml-1 h-8 overflow-hidden rounded-lg border px-1 py-0 shadow-lg ${
                    effectiveHovered ? 'border-black/20 bg-[#ece3de]' : 'border-white/30 bg-black/80'
                  }`}
                  style={{ transformOrigin: 'left center' }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="relative flex h-full items-center gap-1">
                    {languageOptions.map((opt) => (
                      <motion.button
                        key={opt.code}
                        type="button"
                        whileHover={{ y: -1 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setLanguage(opt.code);
                          setIsLanguageQuickOpen(false);
                        }}
                        className={`relative z-10 flex h-7 min-w-[48px] items-center justify-center rounded-md px-2 text-xs ${
                          language === opt.code
                            ? effectiveHovered
                              ? 'bg-white/80 font-semibold text-[#3d2817]'
                              : 'bg-white/20 font-semibold text-white'
                            : effectiveHovered
                              ? 'text-[#3d2817]/80 hover:bg-black/5'
                              : 'text-white/85 hover:bg-white/10'
                        }`}
                      >
                        <span>{opt.label}</span>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}
